import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useTheme, Grid, Chip, LinearProgress, Card, CardContent, alpha, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import LockIcon from '@mui/icons-material/Lock';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import { EmptyState } from '../../../../components/global';

const LockedEpisodes = () => {
  const theme = useTheme();
  const [selectedProduction, setSelectedProduction] = useState(null);

  const { data: productions } = useSWR('/productions');
  
  const { data, error, isLoading } = useSWR(
    selectedProduction?.id
      ? `/reports/locked-episodes?productionId=${selectedProduction.id}`
      : '/reports/locked-episodes',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Κλειδωμένα Επεισόδια" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const report = data || [];

  const columns = [
    {
      field: 'productionName',
      headerName: 'Παραγωγή',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'totalEpisodes',
      headerName: 'Σύνολο Επεισοδίων',
      flex: 1,
      minWidth: 150,
      align: 'center',
    },
    {
      field: 'lockedEpisodes',
      headerName: 'Κλειδωμένα',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          {params.value.map((ep, idx) => (
            <Chip
              key={idx}
              label={`Επ. ${ep}`}
              size="small"
              icon={<LockIcon />}
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'totalLocked',
      headerName: 'Πλήθος',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
    },
    {
      field: 'lockedPercent',
      headerName: 'Ποσοστό %',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params.value;
        const isHigh = value > 50;
        const isMedium = value > 25 && value <= 50;
        
        const getStatusColor = () => {
          if (isHigh) return theme.palette.warning.main;
          if (isMedium) return theme.palette.info.main;
          return theme.palette.success.main;
        };
        
        return (
          <Box sx={{ width: '100%', px: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={value}
                  sx={{
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: theme.palette.action.hover,
                    overflow: 'hidden',
                    boxShadow: `inset 0 1px 2px ${theme.palette.divider}`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(),
                      borderRadius: 2,
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: `0 0 8px ${getStatusColor()}40`,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: getStatusColor(),
                  fontSize: '0.95rem',
                  minWidth: '45px',
                  textAlign: 'right',
                }}
              >
                {value.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'availableEpisodes',
      headerName: 'Διαθέσιμα',
      flex: 1,
      minWidth: 120,
      align: 'center',
    },
    {
      field: 'attemptedExpenses',
      headerName: 'Συνολικά Έξοδα Κλειδωμένων',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
  ];

  const totalLocked = report.reduce((sum, r) => sum + r.totalLocked, 0);
  const totalEpisodes = report.reduce((sum, r) => sum + r.totalEpisodes, 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Κλειδωμένα Επεισόδια" module="Αναφορές" subtitle="Αναφορά κλειδωμένων επεισοδίων" />

      {/* Production Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <TheatersOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: 'right',
                  }}
                >
                  {report.length}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {report.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Παραγωγές με Κλειδωμένα
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.warning.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.warning.main,
                    textAlign: 'right',
                  }}
                >
                  {totalLocked}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.warning.main,
                  mb: 0.5,
                }}
              >
                {totalLocked}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Σύνολο Κλειδωμένων
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.info.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                  }}
                >
                  <MovieCreationOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: 'right',
                  }}
                >
                  {totalEpisodes}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {totalEpisodes}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Σύνολο Επεισοδίων
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease-in-out',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[6],
                borderColor: theme.palette.secondary.main,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                  }}
                >
                  <PercentIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.secondary.main,
                    textAlign: 'right',
                  }}
                >
                  {totalEpisodes > 0 ? ((totalLocked / totalEpisodes) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {totalEpisodes > 0 ? ((totalLocked / totalEpisodes) * 100).toFixed(1) : 0}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Ποσοστό Κλειδωμένων
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3], height: 'calc(100vh -  470px)', display: 'flex', flexDirection: 'column' }}>
        <FormListToolbar disableAdd />
        <Box sx={{ flex: 1, width: '100%', minHeight: 0 }}>
          <DataGrid
            rows={report.map((r, index) => ({ ...r, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              height: '100%',
              width: '100%',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default LockedEpisodes;

